import { useCallback, useMemo } from "react";
import useSWRInfinite from "swr/infinite";
import useUser from "../context/userHook";
import { ItemsPageSlice } from "../../services/monday/monday-query-interface";
import { fetchFirstItemsPage, fetchNextItemsPage } from "../../utils/requests/fetchItemsPage";

const DEFAULT_PAGE_LIMIT = 50;

export type UseItemPageOptions = {
  /** Items per page; Monday max is 500. */
  limit?: number;
  enabled?: boolean;
  searchTerm?: string;
};

type ItemPageCacheKey =
  | readonly ["item-page", number, "first", number, string]
  | readonly ["item-page", number, "next", string, number, string];

export const useItemPage = (currentBoard: number | null, options: UseItemPageOptions = {}) => {
  const { userId } = useUser();
  const { limit = DEFAULT_PAGE_LIMIT, enabled = true, searchTerm = "" } = options;
  const normalizedSearchTerm = searchTerm.trim();

  const getKey = useCallback(
    (pageIndex: number, previousPageData: ItemsPageSlice | null): ItemPageCacheKey | null => {
      if (!enabled || !userId || currentBoard == null) return null;
      if (pageIndex === 0) {
        return ["item-page", currentBoard, "first", limit, normalizedSearchTerm] as const;
      }
      const prevCursor = previousPageData?.cursor;
      if (!prevCursor) return null;
      return ["item-page", currentBoard, "next", prevCursor, limit, normalizedSearchTerm] as const;
    },
    [enabled, userId, currentBoard, limit, normalizedSearchTerm],
  );

  const fetcher = useCallback(async (key: ItemPageCacheKey): Promise<ItemsPageSlice> => {
    if (key[2] === "first") {
      return fetchFirstItemsPage(key[1], key[3], key[4]);
    }
    return fetchNextItemsPage(key[3], key[4]);
  }, []);

  const { data, error, isLoading, isValidating, mutate, size, setSize } = useSWRInfinite(
    getKey,
    fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 2000,
    },
  );

  const items = useMemo(() => (data ? data.flatMap((page) => page.items) : []), [data]);

  const lastPage = data?.[data.length - 1];
  const hasMore = Boolean(lastPage?.cursor);

  const loadMore = useCallback(() => {
    if (!hasMore || isValidating) return;
    void setSize((s) => s + 1);
  }, [hasMore, isValidating, setSize]);

  return {
    items,
    pages: data,
    error,
    isLoading,
    isValidating,
    hasMore,
    loadMore,
    pageCount: size,
    mutate,
    setSize,
  };
};
